import { useCallback, useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import Placeholder from "@tiptap/extension-placeholder";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import { marked } from "marked";
import {
  Bold,
  Heading2,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Table2,
  Youtube as YoutubeIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { htmlToMarkdown, tsvToHtmlTable, clipboardListToHtml } from "@/lib/htmlToMarkdown";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

marked.setOptions({ gfm: true, breaks: true });

function mdToHtml(md: string): string {
  const raw = marked.parse(md || "") as string;
  return typeof raw === "string" ? raw : "";
}

function htmlToMd(html: string): string {
  return htmlToMarkdown(html);
}

function extractYoutubeId(url: string): string | null {
  try {
    const u = new URL(url.trim());
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.replace("/", "") || null;
    }
    if (u.hostname.includes("youtube.com")) {
      return u.searchParams.get("v");
    }
  } catch {
    return null;
  }
  return null;
}

function extractVimeoId(url: string): string | null {
  try {
    const u = new URL(url.trim());
    if (!u.hostname.includes("vimeo.com")) return null;
    const parts = u.pathname.split("/").filter(Boolean);
    return parts[0] && /^\d+$/.test(parts[0]) ? parts[0] : null;
  } catch {
    return null;
  }
}

async function uploadAndInsert(
  editor: Editor,
  file: File,
  documentId: string | undefined,
  setUploading: (v: boolean) => void,
  onUploadImage?: (file: File) => Promise<{ url: string }>,
) {
  setUploading(true);
  try {
    const res = onUploadImage
      ? await onUploadImage(file)
      : await api.docsUploadAsset(file, documentId);
    editor.chain().focus().setImage({ src: res.url, alt: file.name }).run();
  } catch (err) {
    console.error(err);
    window.alert(err instanceof Error ? err.message : "Upload nieudany");
  } finally {
    setUploading(false);
  }
}

type Props = {
  documentId?: string;
  initialMarkdown: string;
  onMarkdownChange: (md: string) => void;
  className?: string;
  /** When false, TipTap and toolbar are read-only (e.g. server unreachable). */
  editable?: boolean;
  /** Override docs upload (e.g. mailing public assets). */
  onUploadImage?: (file: File) => Promise<{ url: string }>;
  /** Hide YouTube embed (email clients ignore iframes). */
  hideVideo?: boolean;
  placeholder?: string;
  minHeightClass?: string;
};

export default function DocMarkdownEditor({
  documentId,
  initialMarkdown,
  onMarkdownChange,
  className,
  editable = true,
  onUploadImage,
  hideVideo = false,
  placeholder = "Pisz tutaj… wklej listę (- / • / 1.), tabelę, obraz lub link YouTube/Vimeo",
  minHeightClass = "min-h-[280px]",
}: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const docIdRef = useRef(documentId);
  const onChangeRef = useRef(onMarkdownChange);
  const onUploadRef = useRef(onUploadImage);
  const editableRef = useRef(editable);
  const skipNextEmit = useRef(false);
  const lastEmitted = useRef(initialMarkdown);
  const [uploading, setUploading] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    docIdRef.current = documentId;
  }, [documentId]);
  useEffect(() => {
    onChangeRef.current = onMarkdownChange;
  }, [onMarkdownChange]);
  useEffect(() => {
    onUploadRef.current = onUploadImage;
  }, [onUploadImage]);
  useEffect(() => {
    editableRef.current = editable;
  }, [editable]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Image.configure({ allowBase64: false }),
      Youtube.configure({
        width: 640,
        height: 360,
        controls: true,
        nocookie: true,
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: { class: "docs-md-table" },
      }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: mdToHtml(initialMarkdown),
    editorProps: {
      attributes: {
        class: cn(
          "docs-editor prose prose-slate max-w-none px-3 py-2 focus:outline-none text-sm leading-relaxed",
          minHeightClass,
        ),
      },
      // true = TipTap nie wkleja sam (Cursor: wygrywamy też capture paste).
      handlePaste: (_view, event) => {
        if (!editableRef.current) {
          event.preventDefault();
          return true;
        }
        const items = event.clipboardData?.items;
        if (items) {
          for (const item of Array.from(items)) {
            if (item.type.startsWith("image/")) return false;
          }
        }
        const html = event.clipboardData?.getData("text/html") || "";
        if (html.includes("<table")) return false;
        const listHtml = clipboardListToHtml(event.clipboardData);
        if (!listHtml) return false;
        event.preventDefault();
        // insertContent w onPaste capture / tu przez view - editorRef ustawiony w onCreate
        const ed = editorRef.current;
        if (!ed) return false;
        ed.chain().focus().insertContent(listHtml).run();
        return true;
      },
    },
    onCreate: ({ editor: ed }) => {
      editorRef.current = ed;
    },
    onUpdate: ({ editor: ed }) => {
      setTick((n) => n + 1);
      if (skipNextEmit.current) {
        skipNextEmit.current = false;
        return;
      }
      const md = htmlToMd(ed.getHTML());
      if (md === lastEmitted.current) return;
      lastEmitted.current = md;
      onChangeRef.current(md);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const onPaste = (event: ClipboardEvent) => {
      if (!editableRef.current) {
        event.preventDefault();
        return;
      }
      const items = event.clipboardData?.items;
      if (items) {
        for (const item of Array.from(items)) {
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              void uploadAndInsert(
                editor,
                file,
                docIdRef.current,
                setUploading,
                onUploadRef.current,
              );
              return;
            }
          }
        }
      }
      const html = event.clipboardData?.getData("text/html") || "";
      // HTML <table> zostaw TipTapowi (rozszerzenie Table).
      if (html.includes("<table")) return;

      // Wypunktowania z Worda / Cursor • / - / 1. → prawdziwe <ul>/<ol>.
      const listHtml = clipboardListToHtml(event.clipboardData);
      if (listHtml) {
        event.preventDefault();
        event.stopImmediatePropagation();
        editor.chain().focus().insertContent(listHtml).run();
        return;
      }

      const text = event.clipboardData?.getData("text/plain")?.trim() || "";
      if (!text) return;

      const tsvTable = tsvToHtmlTable(text);
      if (tsvTable) {
        event.preventDefault();
        editor.chain().focus().insertContent(tsvTable).run();
        return;
      }

      if (hideVideo) return;

      const yt = extractYoutubeId(text);
      if (yt) {
        event.preventDefault();
        editor.commands.setYoutubeVideo({ src: text });
        return;
      }
      const vim = extractVimeoId(text);
      if (vim) {
        event.preventDefault();
        editor
          .chain()
          .focus()
          .insertContent(
            `<iframe src="https://player.vimeo.com/video/${vim}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`,
          )
          .run();
      }
    };
    const onDrop = (event: DragEvent) => {
      if (!editableRef.current) {
        event.preventDefault();
        return;
      }
      const files = event.dataTransfer?.files;
      if (!files?.length) return;
      const file = files[0];
      if (file.type.startsWith("image/")) {
        event.preventDefault();
        void uploadAndInsert(
          editor,
          file,
          docIdRef.current,
          setUploading,
          onUploadRef.current,
        );
      }
    };
    const el = editor.view.dom;
    // capture: wygrywamy z TipTapem (on rejestruje bubble) zanim wklei surowy HTML z Cursora
    el.addEventListener("paste", onPaste, true);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("paste", onPaste, true);
      el.removeEventListener("drop", onDrop);
    };
  }, [editor, hideVideo]);

  useEffect(() => {
    if (!editor) return;
    if (initialMarkdown === lastEmitted.current) return;
    skipNextEmit.current = true;
    lastEmitted.current = initialMarkdown;
    editor.commands.setContent(mdToHtml(initialMarkdown), { emitUpdate: false });
  }, [editor, initialMarkdown]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editor, editable]);

  const setLink = useCallback(() => {
    if (!editor || !editable) return;
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL linku", prev || "https://");
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  }, [editor, editable]);

  const embedVideo = useCallback(() => {
    if (!editor || !editable) return;
    const url = window.prompt("Link YouTube lub Vimeo");
    if (!url?.trim()) return;
    const yt = extractYoutubeId(url);
    if (yt) {
      editor.commands.setYoutubeVideo({ src: url.trim() });
      return;
    }
    const vim = extractVimeoId(url);
    if (vim) {
      editor
        .chain()
        .focus()
        .insertContent(
          `<iframe src="https://player.vimeo.com/video/${vim}" width="640" height="360" frameborder="0" allowfullscreen></iframe>`,
        )
        .run();
      return;
    }
    window.alert("Podaj poprawny link YouTube albo Vimeo");
  }, [editor, editable]);

  if (!editor) return null;

  const toolDisabled = !editable || uploading;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-slate-200 bg-white",
        !editable && "opacity-90",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 bg-slate-50/80 px-2 py-1.5">
        <Button
          type="button"
          size="sm"
          variant={editor.isActive("bold") ? "brand" : "ghost"}
          className="h-8 w-8 p-0"
          disabled={toolDisabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive("italic") ? "brand" : "ghost"}
          className="h-8 w-8 p-0"
          disabled={toolDisabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive("heading", { level: 2 }) ? "brand" : "ghost"}
          className="h-8 w-8 p-0"
          disabled={toolDisabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive("bulletList") ? "brand" : "ghost"}
          className="h-8 w-8 p-0"
          disabled={toolDisabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive("orderedList") ? "brand" : "ghost"}
          className="h-8 w-8 p-0"
          disabled={toolDisabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive("blockquote") ? "brand" : "ghost"}
          className="h-8 w-8 p-0"
          disabled={toolDisabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0"
          disabled={toolDisabled}
          onClick={setLink}
        >
          <Link2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive("table") ? "brand" : "ghost"}
          className="h-8 w-8 p-0"
          title="Wstaw tabelę"
          disabled={toolDisabled}
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
        >
          <Table2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0"
          disabled={toolDisabled}
          onClick={() => fileRef.current?.click()}
          title="Wgraj obraz"
        >
          <ImagePlus className="h-3.5 w-3.5" />
        </Button>
        {!hideVideo ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            disabled={toolDisabled}
            onClick={embedVideo}
          >
            <YoutubeIcon className="h-3.5 w-3.5" />
          </Button>
        ) : null}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          disabled={!editable}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f && editor)
              void uploadAndInsert(
                editor,
                f,
                docIdRef.current,
                setUploading,
                onUploadRef.current,
              );
            e.target.value = "";
          }}
        />
        {uploading ? <span className="ml-2 text-[11px] text-muted-foreground">upload…</span> : null}
        {!editable ? (
          <span className="ml-2 text-[11px] text-amber-700">edycja zablokowana</span>
        ) : null}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
